import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const ContractViewer = ({ contract, onRefresh }) => {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersions, setShowVersions] = useState(false);
  const [modifications, setModifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (contract?.id) {
      loadVersions();
      loadModifications();
    }
  }, [contract]);

  const loadVersions = async () => {
    try {
      const { data, error } = await supabase?.from('contract_versions')?.select(`
          *,
          created_by_profile:user_profiles!contract_versions_created_by_fkey(
            full_name,
            email
          )
        `)?.eq('contract_id', contract?.id)?.order('version_number', { ascending: false });

      if (error) throw error;
      setVersions(data || []);
    } catch (err) {
      console.error('Error loading versions:', err);
    }
  };

  const loadModifications = async () => {
    try {
      const { data, error } = await supabase?.from('contract_modifications')?.select(`
          *,
          modified_by_profile:user_profiles!contract_modifications_modified_by_fkey(
            full_name
          )
        `)?.eq('contract_id', contract?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setModifications(data || []);
    } catch (err) {
      console.error('Error loading modifications:', err);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    alert('سيتم تحميل العقد بصيغة PDF');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'JOD'
    })?.format(amount);
  };

  if (!contract) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Icon name="FileText" size={64} color="#4B5563" className="mx-auto mb-4" />
          <p className="text-gray-500">اختر عقداً لعرض التفاصيل</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-900">
      {/* Toolbar */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          {/* Left - Document Info */}
          <div>
            <h2 className="text-lg font-bold text-white mb-1">
              {contract?.contractNumber || 'عقد جديد'}
            </h2>
            <p className="text-sm text-gray-400">
              {contract?.contractType} - {contract?.property?.title}
            </p>
          </div>

          {/* Right - Tools */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                placeholder="البحث في العقد..."
                className="w-64 h-10 px-4 pr-10 bg-gray-900/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-500"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Icon name="Search" size={16} color="#9CA3AF" />
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-gray-900/50 rounded-lg p-1">
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-700 rounded transition-colors"
                title="تصغير"
              >
                <Icon name="ZoomOut" size={18} color="#9CA3AF" />
              </button>
              <span className="px-3 text-sm text-gray-400 font-medium">{zoom}%</span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-700 rounded transition-colors"
                title="تكبير"
              >
                <Icon name="ZoomIn" size={18} color="#9CA3AF" />
              </button>
            </div>

            {/* Actions */}
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={handleDownload}
            >
              تحميل
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Printer"
              onClick={handlePrint}
            >
              طباعة
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="History"
              onClick={() => setShowVersions(!showVersions)}
            >
              الإصدارات ({versions?.length || 0})
            </Button>
          </div>
        </div>
      </div>
      {/* Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Contract Details Card */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Icon name="FileText" size={20} color="#DAA520" />
              تفاصيل العقد
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">رقم العقد</label>
                <p className="text-white font-medium">{contract?.contractNumber || '-'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">نوع العقد</label>
                <p className="text-white font-medium">{contract?.contractType || '-'}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">تاريخ البدء</label>
                <p className="text-white font-medium">{formatDate(contract?.startDate)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">تاريخ الانتهاء</label>
                <p className="text-white font-medium">{formatDate(contract?.endDate)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">المبلغ</label>
                <p className="text-white font-medium text-lg">{formatCurrency(contract?.amount)}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">حالة التوقيع</label>
                <p className="text-white font-medium">
                  {contract?.signatureStatus === 'pending' && '⏳ قيد الانتظار'}
                  {contract?.signatureStatus === 'signed' && '✅ موقّع'}
                  {contract?.signatureStatus === 'rejected' && '❌ مرفوض'}
                  {contract?.signatureStatus === 'expired' && '⚠️ منتهي'}
                </p>
              </div>
            </div>
          </div>

          {/* Property Details */}
          {contract?.property && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="Building2" size={20} color="#DAA520" />
                تفاصيل العقار
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">اسم العقار</label>
                  <p className="text-white font-medium">{contract?.property?.title || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">نوع العقار</label>
                  <p className="text-white font-medium">{contract?.property?.property_type || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm text-gray-400 block mb-1">العنوان</label>
                  <p className="text-white font-medium">{contract?.property?.address || '-'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Modifications History */}
          {modifications?.length > 0 && (
            <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="History" size={20} color="#DAA520" />
                سجل التعديلات
              </h3>
              <div className="space-y-3">
                {modifications?.map(mod => (
                  <div key={mod?.id} className="flex items-start gap-3 p-3 bg-gray-900/50 rounded-lg">
                    <Icon name="Edit" size={16} color="#F59E0B" className="mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-white font-medium mb-1">
                        {mod?.modification_type}
                      </p>
                      {mod?.field_changed && (
                        <p className="text-xs text-gray-400">
                          {mod?.field_changed}: {mod?.old_value} ← {mod?.new_value}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(mod?.created_at)} - {mod?.modified_by_profile?.full_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Page Navigation */}
      <div className="bg-gray-800 border-t border-gray-700 p-4">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronRight" size={20} color="#9CA3AF" />
          </button>
          <span className="text-sm text-gray-400">
            صفحة {currentPage} من {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="ChevronLeft" size={20} color="#9CA3AF" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractViewer;